import { Box, Typography, useTheme } from "@mui/material";
import { Receipt, MonitorWeight, Inventory2 } from "@mui/icons-material";
import { ReactNode } from "react";
import pluralize from "pluralize";
import { formatNumber } from "../../constants/utils";
import { handlingUnitsTransformer } from "../../utils/handlingUnitsDashTransformer";
import PalletsIcon from "./icons/PalletsIcon";
import PiecesIcon from "./icons/PiecesIcon";
import { iconFillColors } from "./icons/Icon.types";
import type { LoadSummary } from "./EquipmentDropdown.types";

const iconStyles = { fontSize: "1.25rem", color: iconFillColors.darkGray };

const loadMetricIcons = {
  bills: <Receipt sx={iconStyles} />,
  pallets: <PalletsIcon sx={iconStyles} />,
  pieces: <PiecesIcon sx={iconStyles} />,
  handlingUnits: <Inventory2 sx={iconStyles} />,
  weight: <MonitorWeight sx={iconStyles} />,
};

const MetricDisplay = ({
  icon,
  value,
  formattedValue,
  unit,
  disablePluralize = false,
  width,
}: {
  icon: ReactNode;
  value: number;
  formattedValue?: string;
  unit?: string;
  disablePluralize?: boolean;
  width?: number;
}) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "4px",
        flexShrink: 0,
        ...(width ? { width: `${width}px` } : {}),
      }}
      aria-label={unit ? `${unit} ${value}` : `${value}`}
    >
      {icon}
      <Typography
        sx={{
          fontSize: "0.875rem",
          fontWeight: 400,
          color: theme.palette.grey[500],
          lineHeight: "143%",
          letterSpacing: "0.01063rem",
          whiteSpace: "nowrap",
        }}
      >
        {formattedValue ?? formatNumber(value)}
        {unit && ` ${disablePluralize ? unit : pluralize(unit, value)}`}
      </Typography>
    </Box>
  );
};

export const LoadSummaryDisplay = ({
  loadSummary,
}: {
  loadSummary: LoadSummary;
}) => {
  const { bills, pallets, pieces, handlingUnits, weight } = loadSummary;

  return (
    <Box
      display="flex"
      alignItems="center"
      flexWrap="nowrap"
      gap={0.5}
      overflow="hidden"
    >
      <MetricDisplay icon={loadMetricIcons.bills} value={bills} width={40} />
      <MetricDisplay
        icon={loadMetricIcons.pallets}
        value={pallets}
        width={40}
      />
      <MetricDisplay icon={loadMetricIcons.pieces} value={pieces} width={48} />
      <MetricDisplay
        icon={loadMetricIcons.handlingUnits}
        value={handlingUnits || 0}
        formattedValue={handlingUnitsTransformer(handlingUnits || 0)}
        width={48}
      />
      <MetricDisplay
        icon={loadMetricIcons.weight}
        value={weight}
        unit="lb"
        width={90}
      />
    </Box>
  );
};
